import { useState, useMemo, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, Pencil, Trash2, Tag, ChevronDown, ChevronRight, Search } from 'lucide-react';
import { dataService, isDemoMode } from '@/services/data-service';
import { mockAssets } from '@/lib/mock-data';
import { useToast } from '@/hooks/use-toast';
import type { Category, SubCategory, DepreciationMethod, DepreciationFrequency } from '@/types/settings';
import { ProductTour } from '@/components/ui/product-tour';
import { useTour } from '@/contexts/TourContext';
import { useCategoriesTour } from '@/hooks/use-categories-tour';
import { EvoFeatureFeedbackCollector } from '@/components/FeedbackCollector';

const depreciationMethods: DepreciationMethod[] = [
  'Straight Line',
  'Declining Balance',
  'Sum of Years Digits',
  'Units of Production',
];

const depreciationFrequencies: DepreciationFrequency[] = [
  'Monthly',
  'Quarterly',
  'Annually',
];

function createNewSubCategory(
  formData: Partial<SubCategory>,
  parentCategoryId: string
): SubCategory {
  return {
    id: `subcat-${Date.now()}`,
    name: formData.name ?? '',
    notes: formData.notes,
    assetCount: 0,
    parentCategoryId,
    createdBy: 'current-user',
    createdDate: new Date().toISOString(),
  };
}

function countSubCategoryAssets(
  categoryCode: string,
  subCategoryName: string,
  assets: typeof mockAssets
): number {
  return assets.filter(
    (asset) => asset.category === categoryCode && asset.subCategory === subCategoryName
  ).length;
}

interface SubCategoryRowProps {
  readonly subCategory: SubCategory;
  readonly categoryCode: string;
  readonly categoryId: string;
  readonly onAssetCountClick: (categoryCode: string, subCategoryName: string) => void;
  readonly onDelete: (categoryId: string, subCategoryId: string) => void;
}

function SubCategoryRow({
  subCategory,
  categoryCode,
  categoryId,
  onAssetCountClick,
  onDelete,
}: SubCategoryRowProps): React.ReactElement {
  const handleAssetClick = (): void => {
    onAssetCountClick(categoryCode, subCategory.name);
  };

  const handleEditClick = (): void => {
    // Navigate to category detail page instead of opening modal
    window.location.href = `/administration/categories/${categoryId}`;
  };

  const handleDeleteClick = (): void => {
    onDelete(categoryId, subCategory.id);
  };

  return (
    <TableRow className="bg-muted/30 hover:bg-muted/50">
      <TableCell className="font-medium pl-14">
        <div className="flex items-center gap-2">
          <Tag className="h-3 w-3 text-muted-foreground" />
          {subCategory.name}
        </div>
      </TableCell>
      <TableCell colSpan={5} className="text-muted-foreground">
        {subCategory.notes ?? '-'}
      </TableCell>
      <TableCell className="text-center">
        <Button
          variant="ghost"
          size="sm"
          className="h-auto p-0"
          onClick={handleAssetClick}
        >
          <Badge variant="outline" className="cursor-pointer hover:bg-secondary/80">
            {subCategory.assetCount}
          </Badge>
        </Button>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleEditClick}
            title="Edit"
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDeleteClick}
            className="text-destructive hover:text-destructive"
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

export default function CategoriesPage(): React.ReactElement {
  const navigate = useNavigate();
  const { toast } = useToast();
  const tour = useCategoriesTour();
  const { setOnStartTour } = useTour();
  
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [editingSubCategory, setEditingSubCategory] = useState<SubCategory | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const [subCategoryFormData, setSubCategoryFormData] = useState<Partial<SubCategory>>({
    name: '',
    notes: '',
  });

  // Register tour
  useEffect(() => {
    setOnStartTour(() => tour.startTour);
    return () => setOnStartTour(null);
  }, [setOnStartTour, tour]);

  useEffect(() => {
    async function loadCategories() {
      try {
        setLoading(true);
        setError(null);
        const data = await dataService.getCategories();
        setCategories(data);
      } catch (err) {
        console.error('Failed to load categories:', err);
        setError('Failed to load categories. Please try again.');
      } finally {
        setLoading(false);
      }
    }
    loadCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const data = await dataService.getCategories();
      setCategories(data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const categoriesWithAssetCounts = useMemo(() => {
    return categories.map((category) => {
      const assetCount = mockAssets.filter(
        (asset) => asset.category === category.code
      ).length;
      
      const subCategoriesWithCounts = (category.subCategories ?? []).map((subCat) => ({
        ...subCat,
        assetCount: countSubCategoryAssets(category.code, subCat.name, mockAssets),
      }));
      
      return {
        ...category,
        assetCount,
        subCategories: subCategoriesWithCounts,
      };
    });
  }, [categories]);

  const [formData, setFormData] = useState<Partial<Category>>({
    code: '',
    description: '',
    defaultMethod: 'Straight Line',
    defaultPercentage: 0,
    defaultPeriodYears: 0,
    defaultFrequency: 'Monthly',
    userField1: '',
    userField2: '',
    userField3: '',
    doNotDepreciate: false,
    subCategories: [],
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _handleOpenDialog = (category?: Category) => {
    if (category) {
      setEditingCategory(category);
      setFormData(category);
    } else {
      setEditingCategory(null);
      setFormData({
        code: '',
        description: '',
        defaultMethod: 'Straight Line',
        defaultPercentage: 0,
        defaultPeriodYears: 0,
        defaultFrequency: 'Monthly',
        userField1: '',
        userField2: '',
        userField3: '',
        doNotDepreciate: false,
        subCategories: [],
      });
    }
    setSubCategoryFormData({ name: '', notes: '' });
    setEditingSubCategory(null);
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingCategory) {
        if (isDemoMode()) {
          setCategories(
            categories.map((cat) =>
              cat.id === editingCategory.id
                ? { ...cat, ...formData, modifiedDate: new Date().toISOString() }
                : cat
            )
          );
        } else {
          const updated = await dataService.updateCategory(editingCategory.id, {
            ...formData,
            modifiedDate: new Date().toISOString(),
          } as Category);
          setCategories(categories.map((cat) => (cat.id === updated.id ? updated : cat)));
        }
      } else {
        const newCategory: Category = {
          id: String(categories.length + 1),
          code: formData.code ?? '',
          description: formData.description,
          defaultMethod: formData.defaultMethod ?? 'Straight Line',
          defaultPercentage: formData.defaultPercentage ?? 0,
          defaultPeriodYears: formData.defaultPeriodYears ?? 0,
          defaultFrequency: formData.defaultFrequency ?? 'Monthly',
          userField1: formData.userField1,
          userField2: formData.userField2,
          userField3: formData.userField3,
          doNotDepreciate: formData.doNotDepreciate ?? false,
          assetCount: 0,
          createdBy: 'current-user',
          createdDate: new Date().toISOString(),
          subCategories: [],
        };
        
        if (isDemoMode()) {
          setCategories([...categories, newCategory]);
        } else {
          const created = await dataService.createCategory(newCategory);
          setCategories([...categories, created]);
        }
      }
      toast({
        title: 'Success',
        description: editingCategory ? 'Category updated successfully' : 'Category created successfully',
      });
      setIsDialogOpen(false);
    } catch (err) {
      console.error('Failed to save category:', err);
      toast({
        title: 'Error',
        description: 'Failed to save category. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this category? This will also delete all sub-categories.')) {
      return;
    }
    
    try {
      if (isDemoMode()) {
        setCategories(categories.filter((cat) => cat.id !== id));
      } else {
        await dataService.deleteCategory(id);
        setCategories(categories.filter((cat) => cat.id !== id));
      }
      toast({
        title: 'Success',
        description: 'Category deleted successfully',
      });
    } catch (err) {
      console.error('Failed to delete category:', err);
      toast({
        title: 'Error',
        description: 'Failed to delete category. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleAssetCountClick = (categoryCode: string, subCategoryName?: string) => {
    if (subCategoryName) {
      navigate(`/assets?category=${encodeURIComponent(categoryCode)}&subCategory=${encodeURIComponent(subCategoryName)}`);
    } else {
      navigate(`/assets?category=${encodeURIComponent(categoryCode)}`);
    }
  };

  const toggleCategoryExpanded = (categoryId: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const resetSubCategoryForm = () => {
    setEditingSubCategory(null);
    setSubCategoryFormData({
      name: '',
      notes: '',
    });
  };

  const handleAddSubCategory = () => {
    resetSubCategoryForm();
  };

  const handleEditSubCategory = (subCategory: SubCategory) => {
    setEditingSubCategory(subCategory);
    setSubCategoryFormData(subCategory);
  };

  const handleCancelSubCategoryEdit = () => {
    resetSubCategoryForm();
  };

  const handleSaveSubCategoryInline = async () => {
    if (!editingCategory) return;

    if (!isDemoMode()) {
      try {
        if (editingSubCategory) {
          await dataService.updateSubCategory(editingSubCategory.id, subCategoryFormData);
        } else {
          await dataService.createSubCategory(editingCategory.id, subCategoryFormData);
        }
        await fetchCategories();
        const updatedCategories = await dataService.getCategories();
        const updatedCategory = updatedCategories.find((cat: Category) => cat.id === editingCategory.id);
        if (updatedCategory) {
          setFormData(updatedCategory);
        }
        toast({
          title: 'Success',
          description: editingSubCategory ? 'Sub-category updated successfully' : 'Sub-category created successfully',
        });
      } catch (err) {
        console.error('Error saving sub-category:', err);
        toast({
          title: 'Error',
          description: 'Failed to save sub-category. Please try again.',
          variant: 'destructive',
        });
        return;
      }
    } else {
      const updatedSubCategories = editingSubCategory
        ? (formData.subCategories ?? []).map((subCat) =>
            subCat.id === editingSubCategory.id
              ? { ...subCat, ...subCategoryFormData, modifiedDate: new Date().toISOString() }
              : subCat
          )
        : [...(formData.subCategories ?? []), createNewSubCategory(subCategoryFormData, editingCategory.id)];
      
      setFormData({ ...formData, subCategories: updatedSubCategories });
      setCategories(
        categories.map((cat) =>
          cat.id === editingCategory.id
            ? { ...cat, subCategories: updatedSubCategories }
            : cat
        )
      );
    }
    
    handleCancelSubCategoryEdit();
  };

  const handleDeleteSubCategoryInline = async (subCategoryId: string) => {
    if (!editingCategory) return;
    if (!confirm('Are you sure you want to delete this sub-category?')) {
      return;
    }

    if (!isDemoMode()) {
      try {
        await dataService.deleteSubCategory(subCategoryId);
        await fetchCategories();
        const updatedCategories = await dataService.getCategories();
        const updatedCategory = updatedCategories.find((cat: Category) => cat.id === editingCategory.id);
        if (updatedCategory) {
          setFormData(updatedCategory);
        }
        toast({
          title: 'Success',
          description: 'Sub-category deleted successfully',
        });
      } catch (err) {
        console.error('Error deleting sub-category:', err);
        toast({
          title: 'Error',
          description: 'Failed to delete sub-category. Please try again.',
          variant: 'destructive',
        });
      }
    } else {
      const updatedSubCategories = (formData.subCategories ?? []).filter((subCat) => subCat.id !== subCategoryId);
      setFormData({ ...formData, subCategories: updatedSubCategories });
      setCategories(
        categories.map((cat) =>
          cat.id === editingCategory.id
            ? { ...cat, subCategories: updatedSubCategories }
            : cat
        )
      );
      toast({
        title: 'Success',
        description: 'Sub-category deleted successfully',
      });
    }
  };

  const handleDeleteSubCategory = async (parentCategoryId: string, subCategoryId: string) => {
    if (!confirm('Are you sure you want to delete this sub-category?')) {
      return;
    }

    if (!isDemoMode()) {
      try {
        await dataService.deleteSubCategory(subCategoryId);
        await fetchCategories();
        toast({
          title: 'Success',
          description: 'Sub-category deleted successfully',
        });
      } catch (err) {
        console.error('Error deleting sub-category:', err);
        toast({
          title: 'Error',
          description: 'Failed to delete sub-category. Please try again.',
          variant: 'destructive',
        });
      }
    } else {
      setCategories(
        categories.map((cat) =>
          cat.id === parentCategoryId
            ? { ...cat, subCategories: (cat.subCategories ?? []).filter((subCat) => subCat.id !== subCategoryId) }
            : cat
        )
      );
      toast({
        title: 'Success',
        description: 'Sub-category deleted successfully',
      });
    }
  };

  const filteredCategories = categoriesWithAssetCounts.filter((category) =>
    category.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    category.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b bg-card p-6 flex-shrink-0">
        <div className="flex flex-col items-start gap-4 lg:flex-row lg:items-center lg:justify-between" data-tour="categories-header">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Tag className="h-6 w-6 text-primary" />
              Asset Categories
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Define and organise asset categories and subcategories
            </p>
          </div>
                                        <div className="flex gap-2 items-center">
                                          <div className="relative w-80" data-tour="categories-search">
                                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                            <Input
                                              placeholder="Search categories..."
                                              value={searchQuery}
                                              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchQuery(e.target.value)}
                                              className="pl-9"
                                            />
                                          </div>
                                          <Button onClick={() => navigate('/administration/categories/new')} data-tour="categories-new-button">
                                            <Plus className="mr-2 h-4 w-4" />
                                            Add Category
                                          </Button>
                                          <EvoFeatureFeedbackCollector feature="Categories" />
                                        </div>
                  </div>
                </div>

            {/* Content */}
      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        <div className="flex-1 overflow-auto">
          <Table data-tour="categories-table">
            <TableHeader className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
              <TableRow className="border-b-2 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
                <TableHead className="h-12 font-semibold">Code</TableHead>
                <TableHead className="h-12 font-semibold">Description</TableHead>
                <TableHead className="h-12 font-semibold">Method</TableHead>
                <TableHead className="text-right h-12 font-semibold">Rate %</TableHead>
                <TableHead className="text-right h-12 font-semibold">Period (Years)</TableHead>
                <TableHead className="h-12 font-semibold">Frequency</TableHead>
                <TableHead className="text-center h-12 font-semibold">Assets</TableHead>
                <TableHead className="text-right h-12 font-semibold"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(() => {
                if (loading) {
                  return (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-muted-foreground h-32">
                        Loading categories...
                      </TableCell>
                    </TableRow>
                  );
                }
                if (error) {
                  return (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-destructive h-32">
                        {error}
                      </TableCell>
                    </TableRow>
                  );
                }
                if (filteredCategories.length === 0) {
                  return (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-muted-foreground h-32">
                        No categories found
                      </TableCell>
                    </TableRow>
                  );
                }
                return filteredCategories.map((category, index) => (
                  <Fragment key={category.id}>
                    <TableRow className={`border-b border-slate-100 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors ${
                      index % 2 === 0 ? 'bg-white dark:bg-slate-950' : 'bg-slate-50/50 dark:bg-slate-900/50'
                    }`}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {(category.subCategories?.length ?? 0) > 0 ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 p-0"
                              onClick={() => toggleCategoryExpanded(category.id)}
                            >
                              {expandedCategories.has(category.id) ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </Button>
                          ) : (
                            <div className="w-6" />
                          )}
                          <button
                            onClick={() => navigate(`/administration/categories/${category.id}`)}
                            className="text-primary hover:underline cursor-pointer font-medium"
                          >
                            {category.code}
                          </button>
                          {category.doNotDepreciate && (
                            <Badge variant="outline" className="ml-2">
                              No Depreciation
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => navigate(`/administration/categories/${category.id}`)}
                          className="text-primary hover:underline cursor-pointer"
                        >
                          {category.description || '-'}
                        </button>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{category.defaultMethod}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {category.defaultPercentage.toFixed(3)}
                      </TableCell>
                      <TableCell className="text-right">
                        {category.defaultPeriodYears.toFixed(3)}
                      </TableCell>
                      <TableCell>{category.defaultFrequency}</TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-auto p-0"
                          onClick={() => handleAssetCountClick(category.code)}
                        >
                          <Badge variant="secondary" className="cursor-pointer hover:bg-secondary/80">
                            {category.assetCount}
                          </Badge>
                        </Button>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/administration/categories/${category.id}`)}
                            title="Edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(category.id)}
                            className="text-destructive hover:text-destructive"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {expandedCategories.has(category.id) &&
                      category.subCategories?.map((subCategory) => (
                        <SubCategoryRow
                          key={subCategory.id}
                          subCategory={subCategory}
                          categoryCode={category.code}
                          categoryId={category.id}
                          onAssetCountClick={handleAssetCountClick}
                          onDelete={handleDeleteSubCategory}
                        />
                      ))}
                  </Fragment>
                ));
              })()}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? 'Edit Category' : 'Add Category'}
            </DialogTitle>
            <DialogDescription>
              {editingCategory
                ? 'Update the category details and manage sub-categories.'
                : 'Enter the details for the new category.'}
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="details" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="details">Category Details</TabsTrigger>
              <TabsTrigger value="subcategories" disabled={!editingCategory}>
                Sub-Categories ({formData.subCategories?.length ?? 0})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-4 mt-4">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="code">Category Code *</Label>
                    <Input
                      id="code"
                      value={formData.code}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        setFormData({ ...formData, code: e.target.value.toUpperCase() })
                      }
                      placeholder="e.g., CAR PARTS"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Input
                      id="description"
                      value={formData.description}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        setFormData({ ...formData, description: e.target.value })
                      }
                      placeholder="e.g., Automotive parts and components"
                    />
                  </div>
                </div>
              </div>

              {/* Depreciation Defaults */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold">Depreciation Defaults</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="method">Default Method</Label>
                    <Select
                      value={formData.defaultMethod}
                      onValueChange={(value: string) =>
                        setFormData({ ...formData, defaultMethod: value as DepreciationMethod })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {depreciationMethods.map((method) => (
                          <SelectItem key={method} value={method}>
                            {method}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="frequency">Default Frequency</Label>
                    <Select
                      value={formData.defaultFrequency}
                      onValueChange={(value: string) =>
                        setFormData({ ...formData, defaultFrequency: value as DepreciationFrequency })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {depreciationFrequencies.map((freq) => (
                          <SelectItem key={freq} value={freq}>
                            {freq}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="percentage">Default Percentage</Label>
                    <Input
                      id="percentage"
                      type="number"
                      step="0.001"
                      value={formData.defaultPercentage}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        setFormData({ ...formData, defaultPercentage: parseFloat(e.target.value) ?? 0 })
                      }
                      placeholder="e.g., 20.000"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="period">Default Period (Years)</Label>
                    <Input
                      id="period"
                      type="number"
                      step="0.001"
                      value={formData.defaultPeriodYears}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        setFormData({ ...formData, defaultPeriodYears: parseFloat(e.target.value) ?? 0 })
                      }
                      placeholder="e.g., 5.000"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="doNotDepreciate"
                    checked={formData.doNotDepreciate}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, doNotDepreciate: checked })
                    }
                  />
                  <Label htmlFor="doNotDepreciate" className="cursor-pointer">
                    Do Not Depreciate
                  </Label>
                </div>
              </div>

              {/* User Fields */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold">Custom Fields</h3>
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="userField1">User Field 1</Label>
                    <Input
                      id="userField1"
                      value={formData.userField1}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        setFormData({ ...formData, userField1: e.target.value })
                      }
                      placeholder="Custom field 1"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="userField2">User Field 2</Label>
                    <Input
                      id="userField2"
                      value={formData.userField2}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        setFormData({ ...formData, userField2: e.target.value })
                      }
                      placeholder="Custom field 2"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="userField3">User Field 3</Label>
                    <Input
                      id="userField3"
                      value={formData.userField3}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        setFormData({ ...formData, userField3: e.target.value })
                      }
                      placeholder="Custom field 3"
                    />
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="subcategories" className="space-y-4 mt-4">
              {editingCategory && (
                <>
                  <Card>
                    <CardHeader className="pb-[15px]">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">Sub-Categories</CardTitle>
                        <Button size="sm" onClick={handleAddSubCategory}>
                          <Plus className="mr-2 h-4 w-4" />
                          Add Sub-Category
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      {/* Sub-Category Form */}
                      {(editingSubCategory || subCategoryFormData.name !== undefined) && (
                        <div className="border-b p-4 space-y-4 bg-muted/30">
                          <h4 className="text-sm font-medium">
                            {editingSubCategory ? 'Edit Sub-Category' : 'New Sub-Category'}
                          </h4>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="subCategoryName">Name *</Label>
                              <Input
                                id="subCategoryName"
                                value={subCategoryFormData.name ?? ''}
                                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                                  setSubCategoryFormData({ ...subCategoryFormData, name: e.target.value })
                                }
                                placeholder="e.g., Brake Parts"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="subCategoryNotes">Notes</Label>
                              <Textarea
                                id="subCategoryNotes"
                                value={subCategoryFormData.notes ?? ''}
                                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                  setSubCategoryFormData({ ...subCategoryFormData, notes: e.target.value })
                                }
                                placeholder="Optional notes..."
                                rows={2}
                              />
                            </div>
                          </div>
                          <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={handleCancelSubCategoryEdit}>
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={handleSaveSubCategoryInline}
                              disabled={!subCategoryFormData.name}
                            >
                              {editingSubCategory ? 'Update' : 'Add'}
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Sub-Categories Table */}
                      <Table>
                        <TableHeader className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
                          <TableRow className="border-b-2 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
                            <TableHead className="h-12 font-semibold pl-6">Name</TableHead>
                            <TableHead className="h-12 font-semibold">Notes</TableHead>
                            <TableHead className="text-center h-12 font-semibold">Assets</TableHead>
                            <TableHead className="text-right h-12 font-semibold pr-6"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(formData.subCategories?.length ?? 0) === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4} className="text-center text-muted-foreground h-32">
                                No sub-categories defined
                              </TableCell>
                            </TableRow>
                          ) : (
                            formData.subCategories?.map((subCategory, index) => (
                              <TableRow 
                                key={subCategory.id}
                                className={`border-b border-slate-100 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors ${
                                  index % 2 === 0 ? 'bg-white dark:bg-slate-950' : 'bg-slate-50/50 dark:bg-slate-900/50'
                                }`}
                              >
                                <TableCell className="font-medium pl-6">{subCategory.name}</TableCell>
                                <TableCell className="text-muted-foreground">
                                  {subCategory.notes ?? '-'}
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge variant="outline">{subCategory.assetCount}</Badge>
                                </TableCell>
                                <TableCell className="text-right pr-6">
                                  <div className="flex justify-end gap-2">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleEditSubCategory(subCategory)}
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleDeleteSubCategoryInline(subCategory.id)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!formData.code}>
              {editingCategory ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ProductTour
        steps={tour.tourSteps}
        isOpen={tour.isTourOpen}
        onClose={tour.closeTour}
        onComplete={tour.completeTour}
      />

    </div>
  );
}
